import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Filter,
  Loader2,
  Plus,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { listAdminCourses } from "@/lib/courses";
import {
  PAYMENT_LABELS,
  PLAN_LABELS,
  STATUS_LABELS,
  adminCreateEnrollment,
  adminDeleteEnrollment,
  adminEnrollmentStats,
  adminListEnrollments,
  adminUpdateEnrollment,
  fmtDate,
  paymentBadgeClass,
  statusBadgeClass,
} from "@/lib/enrollments";

const STATUS_OPTIONS = ["all", "mapped", "awaiting_payment", "active", "cancelled"];
const PAYMENT_OPTIONS = ["all", "unpaid", "partial", "paid"];

export default function AdminEnrollments() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);

  // filters
  const [fStudent, setFStudent] = useState("all");
  const [fTeacher, setFTeacher] = useState("all");
  const [fCourse, setFCourse] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fPay, setFPay] = useState("all");
  const [fSelfEnrolled, setFSelfEnrolled] = useState(false);
  const [fReferred, setFReferred] = useState(false);

  // dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    student_id: "",
    course_id: "",
    notes: "",
  });

  const [editing, setEditing] = useState(null); // enrollment row

  useEffect(() => {
    Promise.all([
      api.get("/admin/users", { params: { role: "student", status: "approved" } }),
      api.get("/admin/users", { params: { role: "teacher", status: "approved" } }),
      listAdminCourses(),
    ]).then(([s, t, c]) => {
      setStudents(s.data);
      setTeachers(t.data);
      setCourses(c.filter((cc) => cc.status === "published"));
    });
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (fStudent !== "all") params.student_id = fStudent;
    if (fTeacher !== "all") params.teacher_id = fTeacher;
    if (fCourse !== "all") params.course_id = fCourse;
    if (fStatus !== "all") params.status = fStatus;
    if (fPay !== "all") params.payment_status = fPay;
    Promise.all([adminListEnrollments(params), adminEnrollmentStats()])
      .then(([r, s]) => {
        let rows = r;
        if (fSelfEnrolled) rows = rows.filter((e) => e.self_enrolled);
        if (fReferred) rows = rows.filter((e) => !!e.referral_id);
        setRows(rows);
        setStats(s);
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [fStudent, fTeacher, fCourse, fStatus, fPay, fSelfEnrolled, fReferred]);

  const onCreate = async () => {
    if (!newForm.student_id || !newForm.course_id) {
      toast.error("Pick a student and a course");
      return;
    }
    try {
      await adminCreateEnrollment(newForm);
      toast.success("Mapping created");
      setNewOpen(false);
      setNewForm({ student_id: "", course_id: "", notes: "" });
      load();
    } catch (err) {
      const code = err?.response?.data?.detail || "create_failed";
      const friendly = {
        already_enrolled: "This student is already enrolled in this course.",
        course_not_published: "Course must be published before mapping.",
        student_not_approved: "Student must be approved first.",
        student_not_found: "Student not found.",
        course_not_found: "Course not found.",
      }[code];
      toast.error(friendly || code);
    }
  };

  const onPatch = async (patch) => {
    if (!editing) return;
    try {
      await adminUpdateEnrollment(editing.id, patch);
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (row) => {
    try {
      const res = await adminDeleteEnrollment(row.id);
      toast.success(res.mode === "hard" ? "Mapping removed" : "Mapping cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const studentName = useMemo(() => {
    const m = new Map();
    students.forEach((s) => m.set(s.id, `${s.name} · ${s.email}`));
    return m;
  }, [students]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="admin-enrollments">
      <DashboardHeader subtitle="Admin · Student–course mappings" />
      <main className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <BackButton to="/admin" />
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-600" /> Enrollments
            </h1>
            <p className="text-sm text-stone-500">
              Map students to a single teacher per course. Payment plan + status
              flow through to the student dashboard.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/referrals"
              className="text-sm text-stone-700 hover:text-stone-900 underline hover:no-underline"
              data-testid="admin-enrollments-referrals-link"
            >
              📊 Referral analytics
            </Link>
            <Button
              onClick={() => setNewOpen(true)}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="new-mapping-button"
            >
              <UserPlus className="mr-2 h-4 w-4" /> New mapping
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="enrollment-stats">
          <StatCard label="Total" value={stats?.total ?? "…"} color="stone" />
          <StatCard label="Mapped" value={stats?.mapped ?? "…"} color="blue" />
          <StatCard
            label="Awaiting payment"
            value={stats?.awaiting_payment ?? "…"}
            color="amber"
          />
          <StatCard label="Active" value={stats?.active ?? "…"} color="emerald" />
          <StatCard label="Cancelled" value={stats?.cancelled ?? "…"} color="zinc" />
        </div>

        {/* Filters */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 text-stone-500" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <FilterSelect
                label="Student"
                value={fStudent}
                onChange={setFStudent}
                options={[
                  { value: "all", label: "All students" },
                  ...students.map((s) => ({ value: s.id, label: `${s.name} · ${s.email}` })),
                ]}
                testid="filter-student"
              />
              <FilterSelect
                label="Teacher"
                value={fTeacher}
                onChange={setFTeacher}
                options={[
                  { value: "all", label: "All teachers" },
                  ...teachers.map((t) => ({ value: t.id, label: `${t.name} · ${t.email}` })),
                ]}
                testid="filter-teacher"
              />
              <FilterSelect
                label="Course"
                value={fCourse}
                onChange={setFCourse}
                options={[
                  { value: "all", label: "All courses" },
                  ...courses.map((c) => ({ value: c.id, label: c.title })),
                ]}
                testid="filter-course"
              />
              <FilterSelect
                label="Status"
                value={fStatus}
                onChange={setFStatus}
                options={STATUS_OPTIONS.map((s) => ({
                  value: s,
                  label: s === "all" ? "All statuses" : STATUS_LABELS[s] || s,
                }))}
                testid="filter-status"
              />
              <FilterSelect
                label="Payment"
                value={fPay}
                onChange={setFPay}
                options={PAYMENT_OPTIONS.map((p) => ({
                  value: p,
                  label: p === "all" ? "All payments" : PAYMENT_LABELS[p] || p,
                }))}
                testid="filter-payment"
              />
              <label
                className="inline-flex items-center gap-2 text-sm cursor-pointer select-none border border-stone-200 rounded-md px-3 py-2 bg-white hover:bg-stone-50"
                data-testid="filter-self-enrolled-chip"
              >
                <input
                  type="checkbox"
                  checked={fSelfEnrolled}
                  onChange={(e) => setFSelfEnrolled(e.target.checked)}
                  data-testid="filter-self-enrolled"
                  className="accent-amber-600"
                />
                🎫 Self-enrolled only
              </label>
              <label
                className="inline-flex items-center gap-2 text-sm cursor-pointer select-none border border-stone-200 rounded-md px-3 py-2 bg-white hover:bg-stone-50"
                data-testid="filter-referred-chip"
              >
                <input
                  type="checkbox"
                  checked={fReferred}
                  onChange={(e) => setFReferred(e.target.checked)}
                  data-testid="filter-referred"
                  className="accent-purple-600"
                />
                🔗 Referred only
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">
              {rows.length} mapping{rows.length === 1 ? "" : "s"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-10 text-center text-stone-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </div>
            ) : rows.length === 0 ? (
              <div
                className="py-10 text-center text-sm text-stone-500"
                data-testid="no-enrollments"
              >
                No mappings match these filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="enrollments-table">
                  <thead className="bg-stone-50 text-stone-600 text-xs uppercase">
                    <tr>
                      <th className="text-left py-2 px-3">Student</th>
                      <th className="text-left py-2 px-3">Course</th>
                      <th className="text-left py-2 px-3">Teacher</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-left py-2 px-3">Payment</th>
                      <th className="text-left py-2 px-3">Plan</th>
                      <th className="text-left py-2 px-3">Amount</th>
                      <th className="text-left py-2 px-3">Mapped</th>
                      <th className="text-right py-2 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-stone-100"
                        data-testid={`enrollment-row-${r.id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="font-medium">{r.student_name}</div>
                          <div className="text-xs text-stone-500">{r.student_email}</div>
                        </td>
                        <td className="py-2 px-3 max-w-[200px] truncate">{r.course_title}</td>
                        <td className="py-2 px-3 text-stone-600">{r.teacher_name}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className={statusBadgeClass(r.status)}>
                              {STATUS_LABELS[r.status]}
                            </Badge>
                            {r.self_enrolled && (
                              <Badge
                                variant="outline"
                                className="border-amber-300 text-amber-800 bg-amber-50 text-[10px]"
                                data-testid={`self-enrolled-pill-${r.id}`}
                                title="Student self-enrolled via public catalog"
                              >
                                🎫 Self-enrolled
                              </Badge>
                            )}
                            {r.referral_id && (
                              <Badge
                                variant="outline"
                                className="border-purple-300 text-purple-800 bg-purple-50 text-[10px]"
                                data-testid={`referred-pill-${r.id}`}
                                title="Student came in via a friend's referral link"
                              >
                                🔗 Referred
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <Badge
                            variant="outline"
                            className={paymentBadgeClass(r.payment_status)}
                          >
                            {PAYMENT_LABELS[r.payment_status]}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {r.plan_chosen ? PLAN_LABELS[r.plan_chosen] : "—"}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {r.amount_paid > 0
                            ? `${r.currency || ""} ${r.amount_paid}`
                            : "—"}
                        </td>
                        <td className="py-2 px-3 text-xs text-stone-500">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="py-2 px-3 text-right space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditing(r)}
                            data-testid={`edit-${r.id}`}
                          >
                            Edit
                          </Button>
                          <DeleteAction row={r} onDelete={() => onDelete(r)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New mapping dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New student–course mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Student</Label>
              <Select
                value={newForm.student_id || ""}
                onValueChange={(v) => setNewForm((f) => ({ ...f, student_id: v }))}
              >
                <SelectTrigger data-testid="new-student-select">
                  <SelectValue placeholder="Pick a student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {s.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Course (published only)</Label>
              <Select
                value={newForm.course_id || ""}
                onValueChange={(v) => setNewForm((f) => ({ ...f, course_id: v }))}
              >
                <SelectTrigger data-testid="new-course-select">
                  <SelectValue placeholder="Pick a course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea
                value={newForm.notes}
                onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Scholarship student"
                data-testid="new-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onCreate}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="new-create-button"
            >
              <Plus className="mr-1 h-4 w-4" /> Create mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit enrollment</DialogTitle>
          </DialogHeader>
          {editing && (
            <EditForm row={editing} students={studentName} onSave={onPatch} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const bg = {
    stone: "bg-stone-50 border-stone-200",
    blue: "bg-blue-50 border-blue-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    zinc: "bg-zinc-50 border-zinc-200",
  }[color];
  return (
    <Card className={`${bg} border`} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <CardContent className="py-3">
        <div className="text-xs uppercase text-stone-500">{label}</div>
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DeleteAction({ row, onDelete }) {
  const needsConfirm = row.status === "active" && row.payment_status === "paid";
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-red-300 text-red-700"
          data-testid={`delete-${row.id}`}
        >
          <XCircle className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {needsConfirm
              ? "This student has paid — confirm cancellation?"
              : row.status === "mapped" && row.payment_status === "unpaid"
                ? "Remove this mapping?"
                : "Cancel this enrollment?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {needsConfirm
              ? "Their access will be revoked; payment history is preserved."
              : row.status === "mapped" && row.payment_status === "unpaid"
                ? "Mapping is unpaid — it can be deleted cleanly."
                : "The enrollment will be set to cancelled."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-red-600 hover:bg-red-700"
            data-testid={`confirm-delete-${row.id}`}
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditForm({ row, onSave }) {
  const [form, setForm] = useState({
    status: row.status,
    payment_status: row.payment_status,
    plan_chosen: row.plan_chosen || "none",
    amount_paid: row.amount_paid || 0,
    currency: row.currency || "INR",
    notes: row.notes || "",
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
          >
            <SelectTrigger data-testid="edit-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["mapped", "awaiting_payment", "active", "cancelled"].map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Payment</Label>
          <Select
            value={form.payment_status}
            onValueChange={(v) => setForm((f) => ({ ...f, payment_status: v }))}
          >
            <SelectTrigger data-testid="edit-payment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["unpaid", "partial", "paid"].map((p) => (
                <SelectItem key={p} value={p}>
                  {PAYMENT_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Plan</Label>
          <Select
            value={form.plan_chosen}
            onValueChange={(v) => setForm((f) => ({ ...f, plan_chosen: v }))}
          >
            <SelectTrigger data-testid="edit-plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {["pay_in_full", "part_payment", "monthly"].map((p) => (
                <SelectItem key={p} value={p}>
                  {PLAN_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Amount paid</Label>
          <Input
            type="number"
            min={0}
            value={form.amount_paid}
            onChange={(e) =>
              setForm((f) => ({ ...f, amount_paid: Number(e.target.value) || 0 }))
            }
            data-testid="edit-amount"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          data-testid="edit-notes"
        />
      </div>
      <DialogFooter>
        <Button
          onClick={() =>
            onSave({
              status: form.status,
              payment_status: form.payment_status,
              plan_chosen: form.plan_chosen === "none" ? null : form.plan_chosen,
              amount_paid: form.amount_paid,
              currency: form.currency,
              notes: form.notes,
            })
          }
          className="bg-stone-900 hover:bg-stone-800 text-white"
          data-testid="edit-save"
        >
          Save
        </Button>
      </DialogFooter>
    </div>
  );
}
